import { createServerFn } from "@tanstack/react-start";
import { zoneForCoords, type MrPandaZone } from "./mrpanda-zone";

const THESSALONIKI_CITY_NAMES = [
  "thessaloniki",
  "salonika",
  "salonica",
  "θεσσαλονικη",
];

function normalizedHeader(value: string | null): string {
  if (!value) return "";
  try {
    return decodeURIComponent(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  } catch {
    return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  }
}

/** Αναγνωρίζει την πόλη όταν ο edge provider στέλνει όνομα αλλά όχι συντεταγμένες. */
function isThessalonikiFromHeaders(h: Headers): boolean {
  const location = [
    h.get("cf-ipcity"),
    h.get("x-vercel-ip-city"),
    h.get("x-appengine-city"),
    h.get("x-geo-city"),
    h.get("cf-region"),
    h.get("x-vercel-ip-country-region"),
  ]
    .map(normalizedHeader)
    .join(" ");
  return THESSALONIKI_CITY_NAMES.some((name) => location.includes(name));
}

/** Διαβάζει συντεταγμένες από τα geo headers διαφόρων edge providers. */
function coordsFromHeaders(
  h: Headers,
  cf?: { latitude?: string | number; longitude?: string | number },
): { lat: number; lng: number } | null {
  const lat = Number(
    h.get("cf-iplatitude") ?? h.get("x-vercel-ip-latitude") ?? cf?.latitude ?? NaN,
  );
  const lng = Number(
    h.get("cf-iplongitude") ?? h.get("x-vercel-ip-longitude") ?? cf?.longitude ?? NaN,
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/** Εφεδρικός εντοπισμός μέσω δημόσιου IP geo API (χωρίς κλειδί). */
async function coordsFromIp(h: Headers): Promise<{ lat: number; lng: number } | null> {
  const ip =
    h.get("cf-connecting-ip") ??
    h.get("x-real-ip") ??
    (h.get("x-forwarded-for") ?? "").split(",")[0]?.trim();
  if (!ip || ip.startsWith("127.") || ip.startsWith("::1")) return null;
  try {
    const res = await fetch(`https://ipapi.co/${encodeURIComponent(ip)}/json/`, {
      signal: AbortSignal.timeout(3000),
    });
    if (!res.ok) return null;
    const j = (await res.json()) as { latitude?: number; longitude?: number };
    if (!Number.isFinite(j.latitude) || !Number.isFinite(j.longitude)) return null;
    return { lat: Number(j.latitude), lng: Number(j.longitude) };
  } catch {
    return null;
  }
}

// Εντοπισμός περιοχής χρήστη από τα geo headers του edge (χωρίς prompt).
export const detectMrPandaZone = createServerFn({ method: "GET" }).handler(
  async (): Promise<MrPandaZone | null> => {
    try {
      const { getRequest } = await import("@tanstack/react-start/server");
      const request = getRequest() as Request & {
        cf?: { latitude?: string | number; longitude?: string | number };
      };
      const h = request.headers;
      const coords = coordsFromHeaders(h, request.cf) ?? (await coordsFromIp(h));
      if (!coords) {
        // Η πόλη από μόνη της δεν δίνει ακριβές tier. Επιστρέφουμε τη
        // συντηρητική ζώνη των 40 km και το checkout υπολογίζει την πραγματική.
        if (isThessalonikiFromHeaders(h)) {
          return { serviceable: true, distanceKm: 40, freeOver: 120, cost: 12 };
        }
        return null;
      }
      return zoneForCoords(coords.lat, coords.lng);
    } catch {
      return null;
    }
  },
);
